/**
 * Recent items tracking utility functions
 *
 * Provides a generic system for tracking recently visited items in localStorage.
 * Supports multiple categories (apps, groups, etc.) and pinning functionality.
 *
 * This is essentially an LRU cache with pinning support - pinned items are never evicted.
 */

import {
    RECENT_ITEMS_STORAGE_KEY,
    RecentItemEntry,
    RecentItemsStore,
} from 'lib/constants/recent-items'

/** Maximum number of items to store per category */
const MAX_ITEMS_PER_CATEGORY = 50

/**
 * Get the current recent items store from localStorage
 */
export function getRecentItemsStore(): RecentItemsStore {
    try {
        const stored = localStorage.getItem(RECENT_ITEMS_STORAGE_KEY)
        if (stored) {
            return JSON.parse(stored) as RecentItemsStore
        }
    } catch (error) {
        console.error('Error reading recent items from localStorage:', error)
    }
    return {}
}

/**
 * Save the recent items store to localStorage
 */
function saveRecentItemsStore(store: RecentItemsStore): void {
    try {
        localStorage.setItem(RECENT_ITEMS_STORAGE_KEY, JSON.stringify(store))
    } catch (error) {
        console.error('Error writing recent items to localStorage:', error)
    }
}

/**
 * Find the oldest unpinned item in a category store
 * Returns the id of the oldest unpinned item, or undefined if all items are pinned
 */
function findOldestUnpinnedItem(categoryStore: Record<string, RecentItemEntry>): string | undefined {
    let oldestId: string | undefined
    let oldestTimestamp = Infinity

    for (const entry of Object.values(categoryStore)) {
        if (!entry.isPinned && entry.lastVisited < oldestTimestamp) {
            oldestTimestamp = entry.lastVisited
            oldestId = entry.id
        }
    }

    return oldestId
}

/**
 * Record a visit to an item
 * Updates the lastVisited timestamp, creates entry if it doesn't exist
 * Does not modify isPinned status if entry already exists
 *
 * Enforces a max of MAX_ITEMS_PER_CATEGORY items per category.
 * When at capacity, evicts the oldest unpinned item.
 * If all items are pinned, the visit is not recorded (pinned items are never evicted).
 */
export function recordVisit(category: string, id: string): void {
    const store = getRecentItemsStore()

    if (!store[category]) {
        store[category] = {}
    }

    const categoryStore = store[category]
    const existing = categoryStore[id]
    const isNewItem = !existing

    // If adding a new item and at capacity, need to evict
    if (isNewItem && Object.keys(categoryStore).length >= MAX_ITEMS_PER_CATEGORY) {
        const oldestUnpinnedId = findOldestUnpinnedItem(categoryStore)

        if (oldestUnpinnedId) {
            // Evict oldest unpinned item
            delete categoryStore[oldestUnpinnedId]
        } else {
            // All items are pinned - can't evict, don't record this visit
            return
        }
    }

    categoryStore[id] = {
        id,
        lastVisited: Date.now(),
        isPinned: existing?.isPinned ?? false,
    }

    saveRecentItemsStore(store)
}

export type GetRecentItemsOptions = {
    maxItems?: number
}

/**
 * Get recent items for a category
 *
 * @param category - The category to get items for (e.g., 'apps', 'groups')
 * @param options.maxItems - Maximum number of items to return (pinned items count toward this limit)
 *
 * @returns Array of RecentItemEntry sorted by: pinned first (by recency), then unpinned (by recency)
 */
export function getRecentItems(
    category: string,
    options: GetRecentItemsOptions = {},
): RecentItemEntry[] {
    const { maxItems } = options
    const store = getRecentItemsStore()
    const categoryStore = store[category]

    if (!categoryStore) {
        return []
    }

    const entries = Object.values(categoryStore)

    // Sort by: pinned first (by recency), then unpinned (by recency)
    const sortedEntries = entries.sort((a, b) => {
        // Pinned items come first
        if (a.isPinned && !b.isPinned) return -1
        if (!a.isPinned && b.isPinned) return 1
        // Within same pinned status, sort by lastVisited (most recent first)
        return b.lastVisited - a.lastVisited
    })

    if (maxItems === undefined) {
        return sortedEntries
    }

    return sortedEntries.slice(0, maxItems)
}

/**
 * Pin an item
 * Creates entry if it doesn't exist.
 *
 * Enforces MAX_ITEMS_PER_CATEGORY capacity when creating a new pinned item.
 * When at capacity, evicts the oldest unpinned item.
 * If all items are pinned, the pin operation is not performed.
 */
export function pinItem(category: string, id: string): void {
    const store = getRecentItemsStore()

    if (!store[category]) {
        store[category] = {}
    }

    const categoryStore = store[category]
    const existing = categoryStore[id]
    const isNewItem = !existing

    // If adding a new item and at capacity, need to evict
    if (isNewItem && Object.keys(categoryStore).length >= MAX_ITEMS_PER_CATEGORY) {
        const oldestUnpinnedId = findOldestUnpinnedItem(categoryStore)

        if (oldestUnpinnedId) {
            // Evict oldest unpinned item
            delete categoryStore[oldestUnpinnedId]
        } else {
            // All items are pinned - can't evict, don't pin this new item
            return
        }
    }

    categoryStore[id] = {
        id,
        lastVisited: existing?.lastVisited ?? Date.now(),
        isPinned: true,
    }

    saveRecentItemsStore(store)
}

/**
 * Unpin an item
 * Does not remove the entry (keeps visit history)
 */
export function unpinItem(category: string, id: string): void {
    const store = getRecentItemsStore()

    if (!store[category]?.[id]) {
        return
    }

    store[category][id] = {
        ...store[category][id],
        isPinned: false,
    }

    saveRecentItemsStore(store)
}

/**
 * Check if an item is pinned
 */
export function isPinned(category: string, id: string): boolean {
    const store = getRecentItemsStore()
    return store[category]?.[id]?.isPinned ?? false
}

/**
 * Clear all items for a category
 */
export function clearCategory(category: string): void {
    const store = getRecentItemsStore()
    delete store[category]
    saveRecentItemsStore(store)
}

/**
 * Remove a specific item from a category
 */
export function removeItem(category: string, id: string): void {
    const store = getRecentItemsStore()

    if (store[category]) {
        delete store[category][id]
        saveRecentItemsStore(store)
    }
}
